import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { MenuItem, MenuItemRequest, ReorderRequest } from '../types'

interface UseItemsOptions {
  refetchInterval?: number
}

export function useItems(restaurantId: number, categoryId: number | null, options?: UseItemsOptions) {
  const queryClient = useQueryClient()

  // Get all items for the restaurant (for when no category is selected)
  const allItemsQuery = useQuery({
    queryKey: ['menu-items', restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<MenuItem[]>(
        `/api/admin/restaurants/${restaurantId}/menu-items`
      )
      return response.data
    },
    enabled: !!restaurantId,
    refetchInterval: options?.refetchInterval,
  })

  // Get items for a specific category
  const categoryItemsQuery = useQuery({
    queryKey: ['menu-items', 'category', categoryId],
    queryFn: async () => {
      const response = await apiClient.get<MenuItem[]>(
        `/api/admin/menu-categories/${categoryId}/items`
      )
      return response.data
    },
    enabled: !!categoryId,
  })

  const createItem = useMutation({
    mutationFn: async ({ categoryId, data }: { categoryId: number; data: MenuItemRequest }) => {
      const response = await apiClient.post<{ id: number }>(
        `/api/admin/menu-categories/${categoryId}/items`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  const updateItem = useMutation({
    mutationFn: async ({ itemId, data }: { itemId: number; data: MenuItemRequest }) => {
      await apiClient.put(`/api/admin/menu-items/${itemId}`, data)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  const deleteItem = useMutation({
    mutationFn: async (itemId: number) => {
      await apiClient.delete(`/api/admin/menu-items/${itemId}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  const reorderItems = useMutation({
    mutationFn: async (data: ReorderRequest) => {
      await apiClient.post('/api/admin/menu-items/reorder', data)
    },
    onMutate: async (data) => {
      if (!categoryId) return

      await queryClient.cancelQueries({ queryKey: ['menu-items', 'category', categoryId] })

      const previousItems = queryClient.getQueryData<MenuItem[]>(['menu-items', 'category', categoryId])

      if (previousItems) {
        const reorderedItems = data.ids.map((id, index) => {
          const item = previousItems.find(i => i.id === id)!
          return { ...item, menuposition: index * 10 }
        })
        queryClient.setQueryData(['menu-items', 'category', categoryId], reorderedItems)
      }

      return { previousItems }
    },
    onError: (_err, _data, context) => {
      if (context?.previousItems && categoryId) {
        queryClient.setQueryData(['menu-items', 'category', categoryId], context.previousItems)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  // Toggle item disabled status
  const toggleDisabled = useMutation({
    mutationFn: async ({ itemId, currentItem }: { itemId: number; currentItem: MenuItem }) => {
      // Toggle disabled: 0 -> 1, 1 -> 0
      const newDisabled = currentItem.disabled === 0 ? 1 : 0
      await apiClient.put(`/api/admin/menu-items/${itemId}`, {
        name: currentItem.name,
        description: currentItem.description,
        takeawayPrice: currentItem.takeawayprice ?? 0,
        takeawayFrom: currentItem.takeawayfrom,
        taxRate: currentItem.taxRate,
        nrOfParts: currentItem.nrofparts,
        allowTakeaway: currentItem.allowtakeaway,
        allowDelivery: currentItem.allowdelivery,
        discount: currentItem.discount,
        extra: currentItem.extra,
        allergensList: currentItem.allergensList,
        disabled: newDisabled,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] })
      void queryClient.invalidateQueries({ queryKey: ['scheduled-availability'] })
    },
  })

  // Use category items when a category is selected, otherwise all items
  const items = categoryId
    ? categoryItemsQuery.data ?? []
    : allItemsQuery.data ?? []

  const isLoading = categoryId
    ? categoryItemsQuery.isLoading
    : allItemsQuery.isLoading

  return {
    items,
    isLoading,
    error: categoryId ? categoryItemsQuery.error : allItemsQuery.error,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    toggleDisabled,
  }
}
